/**
 * VP8.5 - "new since your last visit". The server hands the page the moment this link's previous visit ended
 * (`lastVisitAt`, null on a first visit), each document's arrival (`addedAt`) and each deal's `addedAt` /
 * `changedAt` from the importer. These decide which marks show - nothing here is a figure about a deal, and a
 * first visit shows none: nothing is new "since" a visit that never happened.
 */

export const isAfter = (ts?: string | null, since?: string | null): boolean => {
  if (!ts || !since) return false;
  const a = Date.parse(ts);
  const b = Date.parse(since);
  return Number.isFinite(a) && Number.isFinite(b) && a > b;
};

export interface SinceDoc {
  status?: string;
  addedAt?: string | null;
}

/** A document is new once it can be OPENED - a pending row that appeared is not something to read yet. */
export const docIsNew = (doc: SinceDoc | null | undefined, since?: string | null): boolean =>
  Boolean(doc && doc.status === 'available' && isAfter(doc.addedAt, since));

export type DealMark = 'new' | 'updated' | null;

/** NEW when the deal reached the portal after the last visit; UPDATED when its content or a document did. */
export const dealMark = (
  deal: { addedAt?: string | null; changedAt?: string | null },
  docs: Array<SinceDoc | null | undefined>,
  since?: string | null
): DealMark => {
  if (!since) return null;
  if (isAfter(deal.addedAt, since)) return 'new';
  if (isAfter(deal.changedAt, since) || docs.some((x) => docIsNew(x, since))) return 'updated';
  return null;
};

const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * The one line under the header, or null when nothing is new. The date is the reader's own, in their time zone.
 * `noun` names what the cards are: deals, unless told otherwise (the Buying tab's are buildings, BUY1).
 */
export const sinceLine = (
  counts: { newDeals: number; updatedDeals: number; newDocs: number },
  since: string,
  noun: { one: string; many: string } = { one: 'deal', many: 'deals' }
): string | null => {
  const parts = [
    counts.newDeals ? n(counts.newDeals, `new ${noun.one}`, `new ${noun.many}`) : '',
    counts.updatedDeals ? `${n(counts.updatedDeals, noun.one, noun.many)} updated` : '',
    counts.newDocs ? n(counts.newDocs, 'new document', 'new documents') : '',
  ].filter(Boolean);
  if (!parts.length) return null;
  const when = new Date(since).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Since your last visit on ${when}: ${parts.join(' · ')}.`;
};
